import { BrickCanvas } from '@components/canvas/BrickCanvas'
import { Toolbar } from '@components/toolbar/Toolbar'
import { StatusBar } from '@components/ui/StatusBar'
import { ToastContainer } from '@components/ui/Toast'
import { DebugPanel } from '@components/ui/DebugPanel'
import { useKeyboardShortcuts } from '@hooks/useKeyboardShortcuts'
import { useToast } from '@hooks/useToast'
import { useState } from 'react'

/**
 * Main App Component
 *
 * Entry point for the Brick Builder 3D application.
 * Manages the overall layout and coordinates between:
 * - BrickCanvas (3D rendering layer)
 * - UI components (Toolbar, StatusBar)
 * - Global state management
 * - Keyboard shortcuts (Task 3.2.1)
 *
 * Features:
 * - Centralized keyboard shortcut handling
 * - Toast notifications for user feedback
 * - Responsive layout
 *
 * @component
 */
function App() {
  // Toast notification system (Task 3.2.1)
  const { toasts, showToast, removeToast } = useToast()

  // FPS state for debug panel (Task 4.1.0)
  const [fps, setFps] = useState(60)

  // Keyboard shortcuts with visual feedback (Task 3.2.1)
  useKeyboardShortcuts(showToast)

  // Save/Load callbacks (Task 4.2.3)
  const handleSaveSuccess = () => {
    showToast('Scene saved successfully', 'success')
  }

  const handleLoadSuccess = () => {
    showToast('Scene loaded successfully', 'success')
  }

  const handleLoadError = (error: string) => {
    showToast(`Failed to load scene: ${error}`, 'error')
  }

  const handleLoadConfirm = (callback: () => void) => {
    // Show confirmation via browser confirm dialog
    // TODO: Replace with custom modal in future (Task 4.2.4)
    const confirmed = window.confirm(
      'Loading will replace your current scene. Continue?'
    )
    if (confirmed) {
      callback()
    }
  }

  // TODO: Add error boundaries (Task 1.4.3)

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar - Task 3.1.1 */}
      <Toolbar
        onSaveSuccess={handleSaveSuccess}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        onLoadConfirm={handleLoadConfirm}
      />

      {/* Main 3D Canvas */}
      <BrickCanvas onFPSUpdate={setFps} />

      {/* Status Bar - Task 3.1.3 */}
      <StatusBar />

      {/* Debug Panel - Task 4.1.0 (only visible with ?debug=true) */}
      <DebugPanel fps={fps} />

      {/* Toast Notifications - Task 3.2.1 */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  )
}

export default App
